import { createRouter, createWebHistory } from "vue-router";
import { MAINTENANCE_MODE } from "@/maintenanceMode";

// ---- Admin Panel Routes ----
import AdminMainPage from "@/routes/adminpanel/adminmain.vue";

// ---- Main Store Pages ----
import CartSection from "@/routes/main/cart.vue";
import BookFacility from "@/routes/facility/BookFacility.vue";
import FacilityDetails from "@/routes/facility/FacilityDetails.vue";
import FacilityBooking from "@/routes/facility/FacilityBookingForm.vue";
import CustomerSignIn from "@/routes/main/CustomerLogin.vue";

// ---- Auth ----
import VerifyEmail from "@/routes/auth/VerifyEmail.vue";
import VerifyOTP from "@/routes/auth/VerifyOTP.vue";

// ---- Third-Party / Seller ----
import SellerSignup from "@/routes/thirdparty/SellerSignup.vue";
import SellerDashboard from "@/routes/thirdparty/SellerDashboard.vue";
import SellerAddProduct from "@/routes/thirdparty/SellerProductUpload.vue";
import SellerListing from "@/routes/thirdparty/Listings.vue";
import BuyerProductView from "@/routes/thirdparty/buyer-product-view.vue";
import SellerReviews from "@/routes/thirdparty/SellerReview.vue"
import ListingViews from "@/routes/thirdparty/Views.vue"
import SellerEarnings from "@/routes/thirdparty/Earnings.vue"

// ---- Facility booking & ownership ----
import FacilityOwnerRegistration from "@/routes/facility/RegisterFacility.vue";
import FacilityOwnerDashboard from "@/routes/facility/FacilityDashboard.vue";
import FacilityOwnerEditFacility from "@/routes/facility/EditFacility.vue";

// ---- Match Hub (football live; other sports under match_hub/<sport>/) ----
import {
  FootballTeamRegistration,
  TeamDashboard,
  TeamEdit,
  ViewInvites,
  OrganizeMatch,
  ChallengeTeam,
  TacticsAndFormations,
  MatchDetails,
  PlayerDashboard,
} from "@/routes/match_hub/football";
import {
  BasketballTeamRegistration,
  BasketballTeamDashboard,
  BasketballTeamEdit,
  BasketballOrganizeMatch,
  BasketballChallengeTeam,
  BasketballTacticsAndFormations,
  BasketballPlayerDashboard,
} from "@/routes/match_hub/basketball";
import {
  RugbyTeamRegistration,
  RugbyTeamDashboard,
  RugbyTeamEdit,
  RugbyOrganizeMatch,
  RugbyChallengeTeam,
} from "@/routes/match_hub/rugby";
import {
  VolleyballTeamRegistration,
  VolleyballTeamDashboard,
  VolleyballTeamEdit,
  VolleyballOrganizeMatch,
  VolleyballChallengeTeam,
} from "@/routes/match_hub/volleyball";
import {
  TennisTeamRegistration,
  TennisTeamDashboard,
  TennisTeamEdit,
  TennisOrganizeMatch,
  TennisChallengeTeam,
} from "@/routes/match_hub/tennis";
import { SPORT_REGISTRATION_META } from "@/routes/match_hub/constants";
import { RegisterOfficial, OfficialDashboard } from "@/routes/match_officials";
import { PathfinderIntro } from "@/routes/pathfinder";
import { TheAbleIntro } from "@/routes/theable";
import {
  ParalympicStore,
  ParalympicEquipmentDetail,
  PARALYMPIC_STORE_PATH,
  PARALYMPIC_STORE_ROUTE,
  PARALYMPIC_DETAIL_ROUTE,
} from "@/routes/paralympic";
import { KidsStore, KIDS_STORE_PATH, KIDS_STORE_ROUTE, KIDS_DETAIL_ROUTE, KIDS_CART_PATH, KIDS_CART_ROUTE } from "@/routes/kids";
// ---- Pages / Views ----
import ProductViewer from "@/routes/views/ProductView.vue";
import TournamentLanding from "@/routes/tournament/TournamentLanding.vue";
import TournamentRegistration from "@/routes/tournament/TournamentRegistration.vue";
import TournamentFanbase from "@/routes/tournament/TournamentFanbase.vue";

// --- Legal ---

import TermsOfUse from "@/routes/views/TermsofUse.vue"
import PrivacyPolicy from "@/routes/views/PrivacyPolicy.vue"
import SellerTerms from "@/routes/views/SellerTerms.vue"
import BuyerTerms from "@/routes/views/Buyer'sTerms.vue"
import CommunityGuidelines from "@/routes/views/CommunityGuidelines.vue"
import ReturnPolicy from "@/routes/views/SaleRefund.vue"
import FacilityOwnershipTerms from "@/routes/views/FacilityOwnershipTerms.vue"
import RouteLink from "@/routes/views/SiteMap.vue"

// ---- Store Auth ----
import { useAuthStore } from "../authen/auth";
import { useAdminAuthStore } from "../authen/adminAuth";
import {
  ADMIN_GATEWAY_PATH,
  ADMIN_WORKSPACE_PATH,
  ADMIN_CATALOG_EDIT_BASE,
} from "./adminPaths";
import { buildSignInLocation, getSafeRedirectPath } from "../utils/authRedirect";

// ----Errors
import ErrorDisplay from "@/routes/views/ErrorDisplay.vue"
import Maintenance from "@/routes/views/Maintenance.vue"


const routes = [

  // ─── Public Landing ─────────────────────────────────
  {
    path: "/",
    name: "LandingPage",
    component: () => import("@/routes/landing/LandingPage.vue"),
  },
  {
    path: "/ipromiseafrica/tournament",
    name: "TournamentLanding",
    component: TournamentLanding,
    meta: {
      title: "Create and Manage Football Tournaments Easily",
      description: "Run fixtures, standings, registration, and tournament operations from one mobile-first page.",
    },
  },
  {
    path: "/ipromiseafrica/tournament/fanbase",
    name: "TournamentFanbase",
    component: TournamentFanbase,
    meta: {
      title: "Tournament fanbase",
      description: "Browse football tournaments, prize pools, venues, and registration details.",
    },
  },
  {
    path: "/ipromiseafrica/tournament/fanbase/:tournamentNumber",
    name: "TournamentFanbaseDetail",
    component: () => import("@/routes/tournament/TournamentFanbaseDetail.vue"),
    meta: {
      title: "Tournament details",
      description: "Full tournament information from the IPA fanbase.",
    },
  },
  {
    path: "/ipromiseafrica/tournament/register",
    name: "TournamentRegistration",
    component: TournamentRegistration,
    meta: {
      requiresAuth: true,
      title: "Tournament registration setup",
      description: "Configure tournament format, teams, officials, services, and awards.",
    },
  },
  {
    path: "/ipromiseafrica/tournament/dashboard",
    name: "TournamentHostDashboard",
    component: () => import("@/routes/tournament/TournamentHostDashboard.vue"),
    meta: {
      requiresAuth: true,
      title: "Tournament host dashboard",
      description: "Manage team registrations, officials, and service providers for your tournaments.",
    },
  },
  {
    path: "/ipromiseafrica/tournament/dashboard/:tournamentNumber",
    name: "TournamentHostDashboardDetail",
    component: () => import("@/routes/tournament/TournamentHostDashboard.vue"),
    meta: {
      requiresAuth: true,
      title: "Tournament host dashboard",
    },
  },

  // ─── Internal operations console (obfuscated paths) ─
  {
    path: ADMIN_GATEWAY_PATH,
    name: "AdminLoginPanel",
    component: () => import("@/routes/adminpanel/adminlogin.vue"),
    meta: { requiresAdminGuest: true },
  },
  {
    path: ADMIN_WORKSPACE_PATH,
    name: "AdminMainPage",
    component: AdminMainPage,
    meta: { requiresAdmin: true },
  },

  // ─── Match Organizing ───────────────────────────────
  {
    path: "/ipromiseafrica/pathfinder",
    name: "PathfinderIntro",
    component: PathfinderIntro,
    meta: {
      title: "Pathfinder — Nurturing African talent",
      description:
        "The New Dawn of Nurturing African Talents. Structured youth development in football, basketball, rugby, volleyball, and tennis.",
    },
  },
  {
    path: "/ipromiseafrica/the-able",
    name: "TheAbleIntro",
    component: TheAbleIntro,
    meta: {
      title: "The Able — Paralympic sport in Kenya",
      description:
        "Growing Kenyan paralympic sport through special school partnerships, adaptive equipment, trainer networks, and tournaments.",
    },
  },
  {
    path: PARALYMPIC_STORE_PATH,
    name: PARALYMPIC_STORE_ROUTE,
    component: ParalympicStore,
    meta: {
      title: "Paralympic Equipment Store — The Able",
      description:
        "Browse adaptive sports equipment for Kenyan paralympic athletes, schools, and clubs.",
    },
  },
  {
    path: `${PARALYMPIC_STORE_PATH}/:slug`,
    name: PARALYMPIC_DETAIL_ROUTE,
    component: ParalympicEquipmentDetail,
    meta: {
      title: "Equipment details — Paralympic store",
    },
  },
  {
    path: "/ipromiseafrica/matchup",
    name: "MatchesHub",
    component: () => import("@/routes/match_hub/MatchesHub.vue"),
  },
  {
    path: "/ipromiseafrica/matchup/officials/register",
    name: "MatchOfficialRegistration",
    component: RegisterOfficial,
    meta: {
      requiresAuth: true,
      title: "Register as match official",
      description:
        "Sign up to officiate football, basketball, rugby, volleyball, or tennis fixtures on ToAfrica Match Hub.",
    },
  },
  {
    path: "/ipromiseafrica/matchup/officials/dashboard",
    name: "MatchOfficialDashboard",
    component: OfficialDashboard,
    meta: {
      requiresAuth: true,
      title: "Match officials dashboard",
      description:
        "Browse confirmed fixtures, tournament slots, live game controls, and officiating history on IpromiseAfrica Match Hub.",
    },
  },
  {
    path: "/ipromiseafrica/matchup/league",
    name: "IpromiseLeague",
    component: () => import("@/routes/match_hub/football/IpromiseLeague.vue"),
  },
  {
    path: "/ipromiseafrica/matchup/football/register",
    name: "FootballTeamRegistration",
    component: FootballTeamRegistration,
    meta: {
      title: SPORT_REGISTRATION_META.football.title,
      description: SPORT_REGISTRATION_META.football.description,
    },
  },
  {
    path: "/ipromiseafrica/matchup/basketball/register",
    name: "BasketballTeamRegistration",
    component: BasketballTeamRegistration,
    meta: {
      title: SPORT_REGISTRATION_META.basketball.title,
      description: SPORT_REGISTRATION_META.basketball.description,
    },
  },
  {
    path: "/ipromiseafrica/matchup/rugby/register",
    name: "RugbyTeamRegistration",
    component: RugbyTeamRegistration,
    meta: {
      title: SPORT_REGISTRATION_META.rugby.title,
      description: SPORT_REGISTRATION_META.rugby.description,
    },
  },
  {
    path: "/ipromiseafrica/matchup/volleyball/register",
    name: "VolleyballTeamRegistration",
    component: VolleyballTeamRegistration,
    meta: {
      title: SPORT_REGISTRATION_META.volleyball.title,
      description: SPORT_REGISTRATION_META.volleyball.description,
    },
  },
  {
    path: "/ipromiseafrica/matchup/tennis/register",
    name: "TennisTeamRegistration",
    component: TennisTeamRegistration,
    meta: {
      title: SPORT_REGISTRATION_META.tennis.title,
      description: SPORT_REGISTRATION_META.tennis.description,
    },
  },
  {
    path: "/ipromiseafrica/matchup/register",
    name: "TeamRegistrationPage",
    redirect: { name: "FootballTeamRegistration" },
  },
  {
    path: "/ipromiseafrica/matchup/football/dashboard",
    name: "TeamDashboard",
    component: TeamDashboard,
    meta: { requiresAuth: true },
  },
  { path: "/ipromiseafrica/matchup/dashboard", redirect: "/ipromiseafrica/matchup/football/dashboard" },
  {
    path: "/ipromiseafrica/matchup/basketball/dashboard",
    name: "BasketballTeamDashboard",
    component: BasketballTeamDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/matchup/rugby/dashboard",
    name: "RugbyTeamDashboard",
    component: RugbyTeamDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/matchup/volleyball/dashboard",
    name: "VolleyballTeamDashboard",
    component: VolleyballTeamDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/matchup/tennis/dashboard",
    name: "TennisTeamDashboard",
    component: TennisTeamDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/matchup/basketball/team-edit/:s/edit",
    name: "BasketballTeamEdit",
    component: BasketballTeamEdit,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/matchup/rugby/team-edit/:s/edit",
    name: "RugbyTeamEdit",
    component: RugbyTeamEdit,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/matchup/volleyball/team-edit/:s/edit",
    name: "VolleyballTeamEdit",
    component: VolleyballTeamEdit,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/matchup/tennis/team-edit/:s/edit",
    name: "TennisTeamEdit",
    component: TennisTeamEdit,
    meta: { requiresAuth: true },
  },
  { path: "/ipromiseafrica/matchup/team-edit/:s/edit", name: "TeamEdit", component: TeamEdit, meta: { requiresAuth: true } },
  {
    path: "/ipromiseafrica/matchup/football/dashboard/invite-players/:team_id",
    name: "InvitePlayers",
    redirect: () => ({
      name: "TeamDashboard",
      query: { tab: "invites", compose: "1" },
    }),
  },
  {
    path: "/ipromiseafrica/matchup/basketball/dashboard/invite-players/:team_id",
    name: "BasketballInvitePlayers",
    redirect: () => ({
      name: "BasketballTeamDashboard",
      query: { tab: "invites", compose: "1" },
    }),
  },
  {
    path: "/ipromiseafrica/matchup/dashboard/invite-players/:team_id",
    redirect: (to) =>
      `/ipromiseafrica/matchup/football/dashboard/invite-players/${to.params.team_id}`,
  },
  { path: "/ipromiseafrica/matchup/invites", name: "ViewInvites", component: ViewInvites },
  {
    path: "/ipromiseafrica/matchup/football/organize-match",
    name: "FootballOrganizeMatch",
    component: OrganizeMatch,
    meta: { requiresAuth: true, title: "Discover football teams" },
  },
  {
    path: "/ipromiseafrica/matchup/basketball/organize-match",
    name: "BasketballOrganizeMatch",
    component: BasketballOrganizeMatch,
    meta: { requiresAuth: true, title: "Discover basketball teams" },
  },
  {
    path: "/ipromiseafrica/matchup/rugby/organize-match",
    name: "RugbyOrganizeMatch",
    component: RugbyOrganizeMatch,
    meta: { requiresAuth: true, title: "Discover rugby clubs" },
  },
  {
    path: "/ipromiseafrica/matchup/volleyball/organize-match",
    name: "VolleyballOrganizeMatch",
    component: VolleyballOrganizeMatch,
    meta: { requiresAuth: true, title: "Discover volleyball squads" },
  },
  {
    path: "/ipromiseafrica/matchup/tennis/organize-match",
    name: "TennisOrganizeMatch",
    component: TennisOrganizeMatch,
    meta: { requiresAuth: true, title: "Discover tennis teams" },
  },
  {
    path: "/ipromiseafrica/matchup/organize_match",
    name: "OrganizeMatch",
    redirect: { name: "FootballOrganizeMatch" },
  },
  { path: "/ipromiseafrica/matchup/organize-match", redirect: { name: "FootballOrganizeMatch" } },
  {
    path: "/ipromiseafrica/matchup/basketball/challenge/:team_id",
    name: "BasketballChallengeTeam",
    component: BasketballChallengeTeam,
    props: true,
    meta: { requiresAuth: true, title: "Request basketball game" },
  },
  {
    path: "/ipromiseafrica/matchup/rugby/challenge/:team_id",
    name: "RugbyChallengeTeam",
    component: RugbyChallengeTeam,
    props: true,
    meta: { requiresAuth: true, title: "Challenge rugby team" },
  },
  {
    path: "/ipromiseafrica/matchup/volleyball/challenge/:team_id",
    name: "VolleyballChallengeTeam",
    component: VolleyballChallengeTeam,
    props: true,
    meta: { requiresAuth: true, title: "Challenge volleyball team" },
  },
  {
    path: "/ipromiseafrica/matchup/tennis/challenge/:team_id",
    name: "TennisChallengeTeam",
    component: TennisChallengeTeam,
    props: true,
    meta: { requiresAuth: true, title: "Challenge tennis team" },
  },
  { path: "/ipromiseafrica/matchup/challenge/:team_id", name: "ChallengeTeam", component: ChallengeTeam, props: true, meta: { requiresAuth: true } },
  { path: "/ipromiseafrica/matchup/tactics/:team_id/match/:match_id", name: "TacticsAndFormations", component: TacticsAndFormations, props: true, meta: { requiresAuth: true } },
  {
    path: "/ipromiseafrica/matchup/basketball/tactics/preview",
    name: "BasketballTacticsPreview",
    component: BasketballTacticsAndFormations,
    meta: { requiresAuth: false, title: "Basketball tactics (preview)" },
  },
  {
    path: "/ipromiseafrica/matchup/basketball/tactics/:team_id/match/:match_id",
    name: "BasketballTacticsAndFormations",
    component: BasketballTacticsAndFormations,
    props: true,
    meta: { requiresAuth: true, title: "Basketball tactics" },
  },
  { path: "/ipromiseafrica/matchup/match/:match_id", name: "MatchDetails", component: MatchDetails, props: true, meta: { requiresAuth: true } },
  { path: "/ipromiseafrica/matchup/player/dashboard", name: "PlayerDashboard", component: PlayerDashboard, meta: { requiresAuth: true } },
  {
    path: "/ipromiseafrica/matchup/basketball/player/dashboard",
    name: "BasketballPlayerDashboard",
    component: BasketballPlayerDashboard,
    meta: { requiresAuth: true, title: "Basketball player hub" },
  },

  // ─── Facility ownership ─────────────────────────────
  {
    path: "/ipromiseafrica/facility/owner-registration",
    name: "FacilityOwnerRegistration",
    component: FacilityOwnerRegistration,
  },
  {
    path: "/ipromiseafrica/facility/my-facility",
    name: "FacilityOwnerDashboard",
    component: FacilityOwnerDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/facility/edit",
    name: "FacilityOwnerEditFacility",
    component: FacilityOwnerEditFacility,
    meta: { requiresAuth: true },
  },

  // ─── Store Browsing ─────────────────────────────────
  {
    path: "/ipromiseafrica/store/product/:slug",
    name: "ProductViewer",
    component: ProductViewer,
    props: true,
  },

  {
    path: "/ipromiseafrica/store/wishlist",
    name: "WishList",
    component: () => import("@/routes/views/WishList.vue"),
  },
  {
    path: "/ipromiseafrica/store/my-orders",
    name: "MyOrders",
    component: () => import("@/routes/views/MyOrders.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/store",
    name: "MainPage",
    component: () => import("@/routes/main/mainpage.vue"),
  },
  {
    path: KIDS_STORE_PATH,
    name: KIDS_STORE_ROUTE,
    component: KidsStore,
    meta: {
      title: "Kids Sports Store — IpromiseAfrica",
      description:
        "Sports gear for children at every development stage—early movement, structured play, and advanced training.",
    },
  },
  {
    path: `${KIDS_STORE_PATH}/product/:slug`,
    name: KIDS_DETAIL_ROUTE,
    component: () => import("@/routes/kids/KidsProductDetail.vue"),
    meta: {
      title: "Kids Product — IpromiseAfrica",
    },
  },
  {
    path: KIDS_CART_PATH,
    name: KIDS_CART_ROUTE,
    component: () => import("@/routes/kids/KidsCart.vue"),
    meta: {
      title: "Kids Cart — IpromiseAfrica",
      requiresAuth: true,
    },
  },

  // ─── Cart & Checkout ───────────────────────────────
  {
    path: "/ipromiseafrica/cart",
    name: "CartSection",
    component: CartSection,
    meta: { requiresAuth: true },
  },

  // ─── Resale marketplace (seller / buyer) ─────────────
  {
    path: "/ipromiseafrica/resale",
    name: "MainEntryPage",
    component: () => import("@/routes/thirdparty/MainEntry.vue"),
  },
  { path: "/ipromiseafrica/resale/seller-sign-up", name: "SellerSignup", component: SellerSignup },

  {
    path: "/ipromiseafrica/resale/seller/:seller_number/edit",
    name: "SellerProfileEdit",
    component: () => import("@/routes/thirdparty/SellerProfileEdit.vue"),
    props: true,
    meta: { requiresAuth: true },
  },

  {
    path: "/ipromiseafrica/resale/seller-dashboard",
    name: "SellerDashboard",
    component: SellerDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/resale/seller-dashboard/earnings",
    name: "SellerEarnings",
    component: SellerEarnings,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/resale/seller-dashboard/my_reviews",
    name: "SellerReviews",
    component: SellerReviews,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/resale/seller-dashboard/views",
    name: "ListingViews",
    component: ListingViews,
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/resale/seller-dashboard/settings",
    name: "SellerSettings",
    component: () => import("@/routes/thirdparty/SellerSettings.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/ipromiseafrica/resale/messages",
    name: "SellerMessages",
    component: () => import("@/routes/thirdparty/SellerSettings.vue"),
    meta: { requiresAuth: true },
  },

  { path: "/ipromiseafrica/resale/buyer-product-view", name: "BuyerProductView", component: BuyerProductView },

  {
    path: "/ipromiseafrica/resale/saved-listings",
    name: "SavedListings",
    component: () => import("@/routes/thirdparty/SavedListings.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/ipromiseafrica/resale/product-upload",
    name: "SellerAddProduct",
    component: SellerAddProduct,
    meta: { requiresAuth: true },
  },

  {
    path: "/ipromiseafrica/resale/my-listings",
    name: "SellerListing",
    component: SellerListing,
    meta: { requiresAuth: true },
  },

  {
    path: "/ipromiseafrica/resale/product/:id",
    name: "BuyerSingleProductView",
    component: () => import("../routes/thirdparty/BuyerSingleProductView.vue"),
    props: true,
  },

  // ─── Facility booking ───────────────────────────────
  { path: "/ipromiseafrica/facility", name: "BookFacility", component: BookFacility },
  { path: "/ipromiseafrica/facility/available", name: "FacilityDetails", component: FacilityDetails },
  {
    path: "/ipromiseafrica/facility/installation",
    name: "FacilityInstallationIntro",
    component: () => import("@/routes/facility/FacilityInstallationIntro.vue"),
    meta: {
      title: "Sports facility installation",
      description:
        "IPA flagship installation for football turfs, swimming pools, tennis courts, padel courts, and basketball courts.",
    },
  },
  {
    path: "/ipromiseafrica/facility/:id/booking",
    name: "FacilityBooking",
    component: FacilityBooking,
    props: true,
  },

  // ─── Customer Auth ──────────────────────────────────
  {
    path: "/ipromiseafrica/account",
    name: "CustomerSignup",
    component: () => import("@/routes/main/CustomerSignup.vue"),
    meta: { requiresGuest: true },
  },

  {
    path: "/ipromiseafrica/sign-in",
    name: "CustomerSignIn",
    component: CustomerSignIn,
    meta: { requiresGuest: true },
  },

  // ─── Email Verification ────────────────────────────
  {
    path: "/ipromiseafrica/verify_email",
    name: "VerifyEmail",
    component: VerifyEmail,
    meta: { requiresGuest: true },
  },
  {
    path: "/ipromiseafrica/verify_otp",
    name: "VerifyOTP",
    component: VerifyOTP,
    meta: { requiresGuest: true },
  },

  // ─── General Pages ──────────────────────────────────
  {
    path: "/ipromiseafrica/support",
    name: "SupportPage",
    component: () => import("@/routes/main/Support.vue"),
  },
  // Temporarily disabled: About Us times out when opened from search or a direct visit.
  // Restore with: component: () => import("@/routes/views/AboutUs.vue"),
  {
    path: "/ipromiseafrica/about-us",
    name: "AboutUsPage",
    redirect: { name: "LandingPage" },
  },

  {
    path: "/ipromiseafrica/support/acknowledgement",
    name: "SupportAcknowledgement",
    component: () => import("../routes/views/SupportAcknowledgement.vue"),
  },

  // ─── Internal: catalog item editor ─────────────────
  {
    path: `${ADMIN_CATALOG_EDIT_BASE}/:id`,
    name: "EditProduct",
    component: () => import("../routes/adminpanel/EditProduct.vue"),
    props: true,
    meta: { requiresAdmin: true },
  },

  // ─── Forgot Password ───────────────────────────────
  {
    path: "/ipromiseafrica/account/forgot-password",
    name: "ForgotPassword",
    component: () => import("../routes/auth/ForgotPass.vue"),
  },

  // ---- Legal
  { path: "/ipromiseafrica/legal/internet-services/terms-of-use", name: "TermsOfUse", component: TermsOfUse },
  { path: "/ipromiseafrica/legal/internet-services/privacy-policy", name: "PrivacyPolicy", component: PrivacyPolicy },
    { path: "/ipromiseafrica/legal/buyer-terms", name: "BuyerTerms", component: BuyerTerms },
  { path: "/ipromiseafrica/legal/seller-terms", name: "SellerTerms", component: SellerTerms },
  { path: "/ipromiseafrica/legal/community-guidelines", name: "CommunityGuidelines", component: CommunityGuidelines },
  { path: "/ipromiseafrica/legal/facility-ownership-terms", name: "FacilityOwnershipTerms", component: FacilityOwnershipTerms },
  { path: "/ipromiseafrica/store/return-policy", name: "ReturnPolicy", component: ReturnPolicy },
  { path: "/ipromiseafrica/site-map", name: "RouteLink", component: RouteLink },


  // ---- Error
  { path: "/ipromiseafrica/sign-up-error", name: "ErrorDisplay", component: ErrorDisplay },
  {
    path: "/maintenance",
    name: "Maintenance",
    component: Maintenance,
    meta: {
      title: "Maintenance | IpromiseAfrica",
    },
  },

  // ─── Legacy redirects ─────────────────────────────────
  { path: "/ipa/ipromise-league", redirect: "/ipromiseafrica/matchup/league" },
  { path: "/ipa/matching/dashboard", redirect: "/ipromiseafrica/matchup/football/dashboard" },
  { path: "/ipa/matching", redirect: "/ipromiseafrica/matchup" },
  { path: "/ipromiseafrica/matching/dashboard", redirect: "/ipromiseafrica/matchup/football/dashboard" },
  { path: "/ipromiseafrica/matching", redirect: "/ipromiseafrica/matchup" },
  { path: "/ipromiseafrica/player/dashboard", redirect: "/ipromiseafrica/matchup/player/dashboard" },
  { path: "/ipromiseafrica/book_pitch", redirect: "/ipromiseafrica/facility" },
  { path: "/ipromiseafrica/book_pitch/available_pitch", redirect: "/ipromiseafrica/facility/available" },
  {
    path: "/ipromiseafrica/book_pitch/:id/booking",
    redirect: (to) => `/ipromiseafrica/facility/${to.params.id}/booking`,
  },
  { path: "/ipromiseafrica/pitch-owner-registration", redirect: "/ipromiseafrica/facility/owner-registration" },
  { path: "/ipromiseafrica/pitch/my_pitch", redirect: "/ipromiseafrica/facility/my-facility" },
  { path: "/ipromiseafrica/pitch/edit", redirect: "/ipromiseafrica/facility/edit" },
  { path: "/resale", redirect: "/ipromiseafrica/resale" },
  {
    path: "/resale/:pathMatch(.*)*",
    redirect: (to) => ({
      path: `/ipromiseafrica/resale/${to.params.pathMatch || ""}`.replace(/\/$/, "") || "/ipromiseafrica/resale",
      query: to.query,
      hash: to.hash,
    }),
  },
  { path: "/ipromiseafrica/thirdparty/seller-dashboard/messages", redirect: "/ipromiseafrica/resale/messages" },
  { path: "/ipromiseafrica/thirdparty", redirect: "/ipromiseafrica/resale" },
  {
    path: "/ipromiseafrica/thirdparty/:pathMatch(.*)*",
    redirect: (to) => ({
      path: `/ipromiseafrica/resale/${to.params.pathMatch || ""}`.replace(/\/$/, "") || "/ipromiseafrica/resale",
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: "/ipa/:pathMatch(.*)*",
    redirect: (to) => {
      const rawSegment = to.params.pathMatch;
      const segment = Array.isArray(rawSegment)
        ? rawSegment.join("/")
        : typeof rawSegment === "string"
          ? rawSegment
          : "";
      if (segment === "thirdparty" || segment.startsWith("thirdparty/")) {
        const resalePath = segment.replace(/^thirdparty\/?/, "");
        if (resalePath === "seller-dashboard/messages") {
          return { path: "/ipromiseafrica/resale/messages", query: to.query, hash: to.hash };
        }
        return {
          path: resalePath ? `/ipromiseafrica/resale/${resalePath}` : "/ipromiseafrica/resale",
          query: to.query,
          hash: to.hash,
        };
      }
      if (segment === "matching" || segment.startsWith("matching/")) {
        const matchupPath = segment.replace(/^matching\/?/, "");
        return {
          path: matchupPath
            ? `/ipromiseafrica/matchup/${matchupPath}`
            : "/ipromiseafrica/matchup",
          query: to.query,
          hash: to.hash,
        };
      }
      return {
        path: `/ipromiseafrica/${segment}`.replace(/\/$/, "") || "/ipromiseafrica",
        query: to.query,
        hash: to.hash,
      };
    },
  },
];


const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});


// ------------------------------------------------------
// Route Guard
// ------------------------------------------------------
router.beforeEach((to, from, next) => {
  // Flip MAINTENANCE_MODE in src/maintenanceMode.js
  if (MAINTENANCE_MODE && to.name !== "Maintenance") {
    return next({ name: "Maintenance", replace: true });
  }

  const auth = useAuthStore();
  const adminAuth = useAdminAuthStore();

  // Check customer auth token
  auth.checkToken();
  
  // Check admin auth token
  adminAuth.checkToken();

  // ─── Admin Route Protection ─────────────────────────
  if (to.meta.requiresAdmin) {
    if (!adminAuth.isAuthenticated) {
      return next({
        name: "AdminLoginPanel",
        query: { redirect: to.fullPath },
      });
    }
  }

  // Prevent authenticated admins from accessing login page
  if (to.meta.requiresAdminGuest && adminAuth.isAuthenticated) {
    return next({ name: "AdminMainPage" });
  }

  // Prevent authenticated customers from accessing admin login page
  if (to.meta.requiresAdminGuest && auth.isAuthenticated && !adminAuth.isAuthenticated) {
    return next({ name: "MainPage" });
  }

  // ─── Customer Route Protection ─────────────────────
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return next(buildSignInLocation(to.fullPath));
  }

  if (to.meta.requiresGuest && auth.isAuthenticated) {
    const afterLogin = getSafeRedirectPath(to.query.redirect, null);
    if (afterLogin) {
      return next(afterLogin);
    }
    return next({ name: "MainPage" });
  }

  next();
});


export default router;
